import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useDebounced } from '@/lib/catalogue';
import { useCustomers } from '@/lib/orders';
import { useSession } from '@/lib/session';
import { formatMoney } from '@/lib/utils';

type Gift = {
  tierId: number;
  value: string;
  giftName: string;
  claimedCount: number;
  claimable: boolean;
};
type BalanceGroup = {
  groupId: number;
  groupName: string;
  budget: string;
  spent: string;
  remaining: string;
  gifts: Gift[];
};
type BalanceData = { customer: { id: number; name: string; custNum: string }; groups: BalanceGroup[] };

/**
 * Solde des cadeaux d'un client, par groupe fournisseur : son budget (total
 * d'achat), ce qu'il a déjà pris, le restant, et pour chaque cadeau sa valeur, le
 * nombre déjà remis et s'il est encore disponible. Consultation seule.
 */
export default function RewardBalance({
  customerId: controlledId,
}: { customerId?: number | null } = {}) {
  const { session } = useSession();
  // Côté commis (comptoir) : ne montrer que les fournisseurs avec qui le client a
  // fait affaire (budget > 0). L'admin garde la vue complète (configuration).
  const estComptoir = session?.user.role === 'COMPTOIR';
  const estAdmin = session?.user.role === 'ADMIN';
  // Mode « contrôlé » : le client vient du parent (comptoir à recherche unique).
  const controlled = controlledId !== undefined;
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);
  const [ownCustomerId, setOwnCustomerId] = useState<number | null>(null);
  const customerId = controlled ? (controlledId ?? null) : ownCustomerId;

  const balance = useQuery<BalanceData>({
    queryKey: ['reward-balance', customerId],
    queryFn: () => api(`/api/claims/${customerId}/balance`),
    enabled: customerId !== null,
  });

  if (!controlled && customerId === null) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Solde des cadeaux</h2>
          <p className="text-muted-foreground text-sm">
            Cherchez un client pour voir son budget par fournisseur et les cadeaux encore
            disponibles.
          </p>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, n° de client ou ville…"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
        />

        {recherche && (
          <div className="grid max-w-lg gap-2">
            {clients.data?.customers.map((c) => (
              <button
                key={c.id}
                onClick={() => setOwnCustomerId(c.id)}
                className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
              >
                <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {c.custNum}
                </span>
                <span className="font-medium">{c.name}</span>
              </button>
            ))}
            {!clients.isPending && clients.data?.customers.length === 0 && (
              <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  const data = balance.data;
  // Hors admin : ne montrer que les cadeaux DISPONIBLES (réclamables) ou à BUDGET
  // ÉPUISÉ (déjà remis) ; cacher ceux « hors budget ». L'admin garde la vue complète.
  const cadeauxVisibles = (g: BalanceGroup) =>
    estAdmin ? g.gifts : g.gifts.filter((gift) => gift.claimable || gift.claimedCount > 0);
  const groupes = data
    ? data.groups
        // Commis : uniquement les fournisseurs où le client a acheté (budget > 0).
        .filter((g) => !estComptoir || Number(g.budget) > 0)
        // Hors admin : masquer un fournisseur sans aucun cadeau acquis.
        .filter((g) => estAdmin || cadeauxVisibles(g).length > 0)
    : [];
  // Le client a des fournisseurs/cadeaux configurés, mais aucun auquel il a droit.
  const filtreVide = !estAdmin && (data?.groups.length ?? 0) > 0 && groupes.length === 0;

  return (
    <div className="space-y-5">
      {/* En mode contrôlé, l'entête client (nom + changer de client) est géré par le parent. */}
      {!controlled && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{data?.customer.name ?? 'Client'}</h2>
            {data && (
              <p className="text-muted-foreground font-mono text-sm">{data.customer.custNum}</p>
            )}
          </div>
          <button
            onClick={() => {
              setOwnCustomerId(null);
              setQ('');
            }}
            className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
          >
            ← Autre client
          </button>
        </div>
      )}

      {balance.isPending ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : !data || groupes.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <p className="font-medium">
            {filtreVide ? 'Aucun cadeau acquis' : 'Aucun programme de cadeaux'}
          </p>
          <p className="text-muted-foreground mt-1 text-sm">
            {filtreVide
              ? "Ce client n'a encore atteint le seuil d'aucun cadeau."
              : "Aucun groupe fournisseur avec des cadeaux n'est configuré."}
          </p>
        </div>
      ) : (
        groupes.map((g) => (
          <details key={g.groupId} open className="bg-card group rounded-xl border">
            {/* En-tête repliable : fournisseur + budget restant + chevron. */}
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 p-5 [&::-webkit-details-marker]:hidden">
              <h3 className="font-semibold">{g.groupName}</h3>
              <span className="flex items-center gap-2">
                <span className="text-sm">
                  restant{' '}
                  <span className="text-base font-semibold tabular-nums">
                    {formatMoney(g.remaining)}
                  </span>
                </span>
                <span className="text-muted-foreground transition-transform group-open:rotate-180">
                  ▾
                </span>
              </span>
            </summary>
            <div className="border-t px-5 pt-2 pb-4">
              <p className="text-muted-foreground mb-1 text-xs tabular-nums">
                Budget {formatMoney(g.budget)} · remis {formatMoney(g.spent)}
              </p>
              <ul className="divide-y">
                {cadeauxVisibles(g).map((gift) => (
                <li key={gift.tierId} className="flex items-center gap-3 py-2.5">
                  <span className="text-muted-foreground w-24 shrink-0 tabular-nums">
                    {formatMoney(gift.value)}
                  </span>
                  <span className="flex-1 font-medium">
                    {gift.giftName}
                    {gift.claimedCount > 0 && (
                      <span className="ml-2 rounded bg-green-600/15 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-400">
                        ×{gift.claimedCount} remis
                      </span>
                    )}
                  </span>
                  {gift.claimable ? (
                    <span className="text-xs font-medium text-green-700 dark:text-green-400">
                      disponible
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {gift.claimedCount > 0 ? 'budget épuisé' : 'hors budget'}
                    </span>
                  )}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ))
      )}
    </div>
  );
}
